/**
 * Модалка после оплаты.
 *
 * Отдельное окно, а не тост: это момент, ради которого человек пришёл, —
 * ему нужно увидеть, что именно он получил и до какого числа, и одним
 * нажатием перейти к подключению.
 */
import { useScrollLock } from '@/shared/lib/useScrollLock';
import { formatShortDate } from '@/shared/lib/format';
import { Button } from '@/shared/ui/kit/Button';
import { Icon } from '@/shared/ui/kit/Icon';

import styles from './PaymentSuccess.module.css';
import type { PayResult } from './usePayStars';

interface Props {
  result: PayResult | null;
  /** Номер подписки в списке — так она подписана на остальных экранах. */
  number: number | null;
  onOpen: (subscriptionId: string | null) => void;
  onClose: () => void;
}

function describe(result: PayResult, number: number | null): { title: string; text: string } {
  const payment = result.payment;
  if (!payment) {
    return {
      title: 'Оплата получена',
      text: 'Подписка появится через минуту — мы пришлём сообщение в бот, как только всё будет готово.',
    };
  }
  const until = payment.expires_at ? `до ${formatShortDate(payment.expires_at)}` : 'бессрочно';
  const name = number ? `Подписка ${number}` : 'Подписка';
  return {
    title: 'Оплата прошла',
    text: payment.renewed
      ? `${name} продлена — теперь действует ${until}.`
      : `${number ? `${name} оформлена` : 'Новая подписка оформлена'} — действует ${until}.`,
  };
}

export function PaymentSuccess({ result, number, onOpen, onClose }: Props) {
  useScrollLock(result !== null);
  const open = result !== null;
  const content = result ? describe(result, number) : null;
  const subscriptionId = result?.payment?.subscription_id ?? null;

  return (
    <div
      className={open ? `${styles.backdrop} ${styles.open}` : styles.backdrop}
      onClick={onClose}
      aria-hidden={!open}
    >
      <section
        className={styles.dialog}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="payment-success-title"
        onClick={(event) => event.stopPropagation()}
      >
        <span className={styles.badge} aria-hidden>
          <Icon name="check" size={34} />
        </span>
        <h2 id="payment-success-title" className={styles.title}>
          {content?.title}
        </h2>
        <p className={styles.text}>{content?.text}</p>
        <div className={styles.actions}>
          <Button block onClick={() => onOpen(subscriptionId)}>
            {subscriptionId ? 'Открыть подписку' : 'Мои подписки'}
          </Button>
          <Button block variant="quiet" onClick={onClose}>
            Готово
          </Button>
        </div>
      </section>
    </div>
  );
}
